/**
 * Image API V2 - Admin Panel Integration
 * Centralized image management with cache busting
 */

import axios from 'axios';
import { imageServiceUrl } from "./baseUrl";

// Base URL for image service
const IMAGE_API_URL = imageServiceUrl + '/api/v1/images';

/**
 * Get auth token from localStorage or sessionStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('cc-t') || sessionStorage.getItem('cc-t');
};

/**
 * Map category to backend folder enum values
 */
const mapCategoryToFolder = (category) => {
  const categoryMap = {
    'profile': 'users',
    'challenges': 'challenges',
    'badges': 'badges',
    'products': 'products',
    'blogs': 'blogs',
    'events': 'submissions',
    'ngo-logos': 'users',
    'company-logos': 'users',
    'other': 'misc',
  };

  return categoryMap[category] || 'misc';
};

/**
 * Upload a new image
 * @param {File} file - File object from input
 * @param {string} category - Image category
 * @param {boolean} isPublic - Whether image is public (default: true for admin uploads)
 * @returns {Promise<Object>} - { imageId, url, thumbnailUrl, version }
 */
export const uploadImage = async (file, category = 'other', isPublic = true) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('folder', mapCategoryToFolder(category));
    formData.append('isPublic', isPublic.toString());

    console.log('📤 [IMAGE API] Uploading:', file.name);

    const response = await axios.post(`${IMAGE_API_URL}/upload`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('✅ [IMAGE API] Upload successful:', response.data.data.imageId);

    return {
      imageId: response.data.data.imageId,
      url: response.data.data.url,
      thumbnailUrl: response.data.data.thumbnail?.url,
      version: response.data.data.version,
      dimensions: {
        width: response.data.data.metadata?.width,
        height: response.data.data.metadata?.height
      },
      fileSize: response.data.data.metadata?.size,
    };
  } catch (error) {
    console.error('❌ [IMAGE API] Upload failed:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Update an existing image (creates new version)
 * @param {string} imageId - Existing image ID
 * @param {File} newFile - New file
 * @returns {Promise<Object>}
 */
export const updateImage = async (imageId, newFile) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    const formData = new FormData();
    formData.append('image', newFile);

    console.log('🔄 [IMAGE API] Updating:', imageId);

    const response = await axios.put(`${IMAGE_API_URL}/${imageId}`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log(`✅ [IMAGE API] Updated to v${response.data.data.version}`);

    return {
      imageId: response.data.data.imageId,
      url: response.data.data.url, // NEW URL with version!
      thumbnailUrl: response.data.data.thumbnail?.url,
      version: response.data.data.version,
      oldVersion: response.data.data.oldVersion,
      dimensions: {
        width: response.data.data.metadata?.width,
        height: response.data.data.metadata?.height
      },
      fileSize: response.data.data.metadata?.size,
    };
  } catch (error) {
    console.error('❌ [IMAGE API] Update failed:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Delete an image
 * @param {string} imageId - Image ID
 * @param {boolean} permanent - Hard delete from S3 (default: false)
 * @returns {Promise<Object>}
 */
export const deleteImage = async (imageId, permanent = false) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('Authentication required');
    }

    const url = `${IMAGE_API_URL}/${imageId}${permanent ? '?permanent=true' : ''}`;

    console.log('🗑️ [IMAGE API] Deleting:', imageId);

    const response = await axios.delete(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('✅ [IMAGE API] Deleted');

    return response.data;
  } catch (error) {
    console.error('❌ [IMAGE API] Delete failed:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * List images with filters
 * @param {Object} filters - { category, userId, status, limit, skip }
 * @returns {Promise<Object>}
 */
export const listImages = async (filters = {}) => {
  try {
    const token = getAuthToken();

    const response = await axios.get(IMAGE_API_URL, {
      params: filters,
      headers: token ? {
        'Authorization': `Bearer ${token}`,
      } : {},
    });

    return {
      images: response.data.data,
      pagination: response.data.pagination,
    };
  } catch (error) {
    console.error('❌ [IMAGE API] List failed:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Get image metadata
 * @param {string} imageId
 * @returns {Promise<Object>}
 */
export const getImageMetadata = async (imageId) => {
  try {
    const token = getAuthToken();

    const response = await axios.get(`${IMAGE_API_URL}/${imageId}/metadata`, {
      headers: token ? {
        'Authorization': `Bearer ${token}`,
      } : {},
    });

    return response.data.data;
  } catch (error) {
    console.error('❌ [IMAGE API] Get metadata failed:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Helper: Get image URL with specific size
 * @param {string} imageUrl - Full URL from API
 * @param {string} size - 'thumbnail' | 'small' | 'medium' | 'large' | 'original'
 * @returns {string}
 */
export const getImageUrlWithSize = (imageUrl, size = 'medium') => {
  if (!imageUrl) return null;

  try {
    const url = new URL(imageUrl);
    url.searchParams.set('size', size);
    return url.toString();
  } catch (e) {
    // If not a valid URL, return as-is
    return imageUrl;
  }
};

/**
 * Helper: Get thumbnail URL
 * @param {string} imageUrl
 * @returns {string}
 */
export const getThumbnailUrl = (imageUrl) => {
  if (!imageUrl) return null;

  // If already thumbnail, return as-is
  if (imageUrl.includes('/thumbnail')) return imageUrl;

  // Convert to thumbnail endpoint
  return imageUrl.replace(/\/api\/v1\/images\/([^?]+)/, '/api/v1/images/$1/thumbnail');
};

/**
 * Migration helper: Handle old S3 URLs during transition
 * @param {string} oldUrl - Old S3 URL
 * @returns {string}
 */
export const migrateOldImageUrl = (oldUrl) => {
  // If already using new API, return as-is
  if (oldUrl && oldUrl.includes('/api/v1/images/')) {
    return oldUrl;
  }

  // Return old S3 URL as fallback
  console.warn('⚠️ [IMAGE API] Using old S3 URL:', oldUrl);
  return oldUrl;
};

/**
 * Batch upload images
 * @param {File[]} files - Array of files
 * @param {string} category - Category for all images
 * @returns {Promise<Object[]>}
 */
export const batchUploadImages = async (files, category = 'other') => {
  try {
    const uploadPromises = files.map(file => uploadImage(file, category));
    const results = await Promise.all(uploadPromises);

    console.log(`✅ [IMAGE API] Batch uploaded ${results.length} images`);

    return results;
  } catch (error) {
    console.error('❌ [IMAGE API] Batch upload failed:', error);
    throw error;
  }
};

export default {
  uploadImage,
  updateImage,
  deleteImage,
  listImages,
  getImageMetadata,
  getImageUrlWithSize,
  getThumbnailUrl,
  migrateOldImageUrl,
  batchUploadImages,
};
